/**
 * CategoryBreakdown — per-category PASS/WARN/FAIL summary list on the
 * audit Results page. Each row is clickable (filters the dashboard
 * above by that category) and, when the category has fails/warns,
 * carries a "PDF" button to generate a discipline-scoped report via
 * the async ai_jobs flow.
 *
 * Extracted from ResultsPage.js on Feb 26, 2026 so the parent file
 * stays under the 1k-line ceiling and so this surface is independently
 * testable.
 *
 * Props
 * -----
 *   audit                       - the audit row (we read category_breakdown +
 *                                 validation_profile from it)
 *   selectedCategory            - currently filtered category key, or null
 *   onSelectCategory(key)       - called when a row is clicked
 *   onExportCategoryReport(key) - called when the PDF button fires
 *   categoryReportLoadingFor    - which category (if any) is currently
 *                                 generating a PDF (so we render a spinner
 *                                 only on that row, not block the rest)
 */
import { Loader2, FileText } from 'lucide-react';

export function CategoryBreakdown({
  audit,
  selectedCategory,
  onSelectCategory,
  onExportCategoryReport,
  categoryReportLoadingFor,
}) {
  const categories = Array.isArray(audit?.category_breakdown) ? audit.category_breakdown : [];
  const categoryState = audit?.category_breakdown_state;
  if (categories.length === 0) {
    if (!categoryState || categoryState.available !== false) return null;
    return (
      <div className="card-elevated p-4 mb-6" data-testid="category-breakdown-unavailable">
        <h3 className="text-sm font-semibold text-foreground" data-testid="category-breakdown-title">
          Results by Validation Category
        </h3>
        <p className="text-sm text-muted-foreground mt-2" data-testid="category-breakdown-unavailable-reason">
          {categoryState.reason || 'Category results are unavailable.'}
        </p>
      </div>
    );
  }
  return (
    <div className="card-elevated p-4 mb-6" data-testid="category-breakdown">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-foreground" data-testid="category-breakdown-title">
          Results by Validation Category
        </h3>
        <p className="text-[11px] text-muted-foreground" data-testid="category-hint">
          {selectedCategory
            ? 'Click again to clear · charts above scoped to this category'
            : 'Click a row to filter the dashboard above'}
        </p>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3" data-testid="category-unit-hint">
        Applicable and executed checks by category. Skipped checks are not failures.
      </p>
      <div className="space-y-2">
        {categories.map((cat) => (
          <CategoryRow
            key={cat.category}
            cat={cat}
            isActive={selectedCategory === cat.category}
            onToggle={() =>
              onSelectCategory(selectedCategory === cat.category ? null : cat.category)
            }
            onExport={onExportCategoryReport ? () => onExportCategoryReport(cat.category) : null}
            isExporting={categoryReportLoadingFor === cat.category}
          />
        ))}
      </div>
      {Array.isArray(categoryState?.unavailable_categories)
        && categoryState.unavailable_categories.length > 0 && (
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="category-unavailable-list">
            Unavailable categories: {categoryState.unavailable_categories
              .map((key) => key.replace(/_/g, ' ')).join(', ')}. No sealed executed-check aggregate was recorded.
          </p>
      )}
      {audit.validation_profile
        && Object.values(audit.validation_profile).filter((v) => !v).length > 0 && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Skipped categories (not in this audit&apos;s profile):{' '}
            {Object.entries(audit.validation_profile)
              .filter(([, v]) => !v)
              .map(([k]) => k.replace(/_/g, ' '))
              .join(', ')}
          </p>
      )}
    </div>
  );
}

function CategoryRow({ cat, isActive, onToggle, onExport, isExporting }) {
  const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
  const statusCountAvailable = isNumber(cat.passed)
    && isNumber(cat.warnings)
    && isNumber(cat.failures);
  const passed = statusCountAvailable ? cat.passed : null;
  const warnings = statusCountAvailable ? cat.warnings : null;
  const failures = statusCountAvailable ? cat.failures : null;
  const skipped = isNumber(cat.skipped) ? cat.skipped : null;
  const executed = statusCountAvailable ? passed + warnings + failures : null;
  const passPct = statusCountAvailable && executed > 0 ? (passed / executed) * 100 : null;
  const hasIssues = statusCountAvailable && warnings + failures > 0;
  return (
    <div
      className={`w-full flex items-stretch gap-2 rounded-md border transition-colors ${
        isActive
          ? 'bg-[hsl(var(--accent))]/10 border-[hsl(var(--accent))] ring-1 ring-[hsl(var(--accent))]/40'
          : 'border-border bg-card hover:bg-muted/40 hover:border-[hsl(var(--accent))]/40'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex-1 text-left flex items-center gap-3 p-2 cursor-pointer rounded-l-md"
        aria-pressed={isActive}
        data-testid={`category-${cat.category}`}
        title={statusCountAvailable
          ? `${executed.toLocaleString()} applicable/executed checks (${passed} pass, ${warnings} warn, ${failures} fail)`
          : 'Applicable/executed check counts unavailable'}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid={`category-label-${cat.category}`}>{cat.label}</p>
          <p className="text-[11px] text-muted-foreground" data-testid={`category-counts-${cat.category}`}>
            {statusCountAvailable ? `${executed.toLocaleString()} applicable/executed ${executed === 1 ? 'check' : 'checks'}` : 'Unavailable'}
            {passed > 0 && (
              <>
                {' '}· <span className="text-[hsl(var(--success))]">{passed} pass</span>
              </>
            )}
            {warnings > 0 && (
              <>
                {' '}· <span className="text-[hsl(var(--warning))]">{warnings} warn</span>
              </>
            )}
            {failures > 0 && (
              <>
                {' '}· <span className="text-[hsl(var(--destructive))]">{failures} fail</span>
              </>
            )}
            {statusCountAvailable && skipped > 0 && ` · ${skipped.toLocaleString()} skipped/not applicable`}
            {statusCountAvailable && executed === 0 && skipped > 0 && ' · N/A — skipped only'}
          </p>
        </div>
        {passPct !== null && (
          <span
            className={`text-sm font-semibold ${
              passPct >= 90
                ? 'text-[hsl(var(--success))]'
                : passPct >= 70
                ? 'text-[hsl(var(--warning))]'
                : 'text-[hsl(var(--destructive))]'
            }`}
            data-testid={`category-pass-rate-${cat.category}`}
          >
            {passPct.toFixed(2)}%
          </span>
        )}
        {isActive && (
          <span
            className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))]"
            data-testid={`category-active-flag-${cat.category}`}
          >
            ● Active
          </span>
        )}
      </button>
      {hasIssues && onExport && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExport();
          }}
          disabled={isExporting}
          className="px-3 my-1 mr-1 inline-flex items-center gap-1 text-[11px] font-medium text-foreground/80 border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          title={`Generate a discipline-scoped PDF for ${cat.label} only`}
          data-testid={`category-report-btn-${cat.category}`}
        >
          {isExporting ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" /> Working…
            </>
          ) : (
            <>
              <FileText className="h-3 w-3" /> PDF
            </>
          )}
        </button>
      )}
    </div>
  );
}
